import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import {
  BooleanField,
  ImageField,
  Labeled,
  Link,
  NumberField,
  ReferenceArrayField,
  Show,
  ShowButton,
  Tab,
  TabbedShowLayout,
  TextField,
  useRecordContext,
} from 'react-admin';
import {
  AdminClientDetailsFragment,
  ConfigurationIssue,
  useClientConfigurationIssuesLazyQuery,
} from '../../../graphql/types';
import useClientCoachesQuery from '../../../hooks/useClientCoachesQuery';
import { BORDER_GRAY } from '../../../theme';
import { ListCoaches } from '../components/ListCoaches';
import { WriteOnlyDatagrid } from '../components/WriteOnlyDatagrid';
import * as Labels from './labels';

const ConfigurationIssueItem: React.FC<{ issue: ConfigurationIssue }> = ({
  issue,
}) => {
  const { title, description, link } = issue;
  return (
    <div
      style={{
        border: `1px solid ${BORDER_GRAY}`,
        borderRadius: 4,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <Typography variant="subtitle2">{title}</Typography>
      <p>{description}</p>
      {link && (
        <Link to={link} target="_blank" color="primary">
          Link
        </Link>
      )}
    </div>
  );
};

const ClientCoachTab: React.FC = () => {
  const record = useRecordContext<AdminClientDetailsFragment>();
  const { coaches, loadMore, hasNextPage } = useClientCoachesQuery(record?.id);

  return (
    <ListCoaches
      coaches={coaches}
      clientId={record?.id}
      loadMore={loadMore}
      hasNextPage={hasNextPage}
    />
  );
};

const DiagnosticsTab: React.FC = () => {
  const record = useRecordContext<AdminClientDetailsFragment>();
  const clientId = record?.id;

  const [runningDiagnostics, setRunningDiagnostics] = React.useState(false);
  const [fetchIssues, { data }] = useClientConfigurationIssuesLazyQuery();

  const handleClick = async () => {
    if (!clientId) {
      return;
    }

    setRunningDiagnostics(true);
    await fetchIssues({
      variables: {
        id: clientId,
      },
    });
    setRunningDiagnostics(false);
  };

  const configurationIssues = data?.client?.configurationIssues || [];
  return (
    <>
      <Box>
        <Typography variant="h4">Client Diagnostics</Typography>
        <p>
          Press the button below to perform a series of checks on this client's
          configuration. These checks are not comprehensive and are currently
          focused on the following items:
        </p>
        <ul>
          <li>
            Ensure that the provided MBO Studio ID for each non-central studio
            exists in MBO
          </li>
          <li>Ensure that the client has at least one assignable coach</li>
          <li>
            For clients that have "Uses MBO Registration Flow" enabled this will
            check for the following:
            <ul>
              <li>There are in-person studios available for registration</li>
              <li>
                There is at least one valid location with an assigned Hybrid
                coach
              </li>
              <li>The MBO contract is available for each location</li>
              <li>
                The provided MBO studio ID exists in MBO and has a contact email
              </li>
            </ul>
          </li>
        </ul>
        <Button
          onClick={handleClick}
          loading={runningDiagnostics}
          variant="contained"
        >
          Run Diagnostics
        </Button>
      </Box>
      <Box marginTop={2}>
        {!runningDiagnostics && data && configurationIssues.length === 0 && (
          <Typography variant="body1" color="success.main">
            No configuration issues found.
          </Typography>
        )}
        {!runningDiagnostics && (
          <div style={{ width: 'fit-content' }}>
            {configurationIssues.length > 0 && (
              <Typography variant="body1" color="error.main">
                Found the following configuration issue
                {configurationIssues.length > 1 ? 's' : ''}:
              </Typography>
            )}
            {configurationIssues.map((issue) => (
              <ConfigurationIssueItem issue={issue} key={issue.title} />
            ))}
          </div>
        )}
      </Box>
    </>
  );
};

export const ClientShow: React.FC = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Basics">
        <TextField source="id" label="ID" data-testid="id" />
        <TextField source="name" label="Client Name" data-testid="name" />
        <TextField
          source="displayName"
          label="Display Name"
          data-testid="displayName"
        />
        <ImageField source="logo.url" label="Logo" />
        <Typography variant="subtitle2">
          {Labels.SUBTITLE_INTEGRATIONS}
        </Typography>
        <TextField
          source="calendarPlatform"
          label="Calendar Platform"
          data-testid="calendarPlatform"
        />
        <TextField
          source="videoPlatform"
          label="Video Platform"
          data-testid="videoPlatform"
        />

        <Typography variant="subtitle2">
          {Labels.SUBTITLE_OPERATIONS}
        </Typography>
        <BooleanField
          source="isArchived"
          label={Labels.IS_ARCHIVED}
          data-testid="isArchived"
        />
        <BooleanField
          source="isFcmOnly"
          label={Labels.IS_FCM_ONLY}
          data-testid="isFcmOnly"
        />
        <BooleanField
          source="usesSSO"
          data-testid="usesSSO"
          label={Labels.USES_SSO}
        />

        <Typography variant="subtitle2">Linked Entities</Typography>
        <Labeled label="Domains" fullWidth>
          <ReferenceArrayField source="domains.ids" reference="Domain">
            <WriteOnlyDatagrid>
              <TextField source="domain" />
              <ShowButton />
            </WriteOnlyDatagrid>
          </ReferenceArrayField>
        </Labeled>
        <Labeled label="Studios" fullWidth>
          <ReferenceArrayField source="studios.ids" reference="Studio">
            <WriteOnlyDatagrid>
              <TextField source="name" />
              <BooleanField source="isVirtual" label="Virtual" />
              <BooleanField source="isArchived" label="Archived" />
              <ShowButton />
            </WriteOnlyDatagrid>
          </ReferenceArrayField>
        </Labeled>
      </Tab>
      <Tab label="Configs" data-testid="tab-Fit">
        <Typography variant="subtitle2">
          {Labels.SUBTITLE_ELIGIBILITY}
        </Typography>
        <BooleanField
          source="usesFitnessCenterForEligibility"
          data-testid="usesFitnessCenterForEligibility"
          label={Labels.USES_FITNESS_CENTER_FOR_ELIGIBILITY}
        />
        <BooleanField
          source="requiresEligibleEmailForGym"
          data-testid="requiresEligibleEmailForGym"
          label={Labels.REQUIRES_ELIGIBLE_EMAIL_FOR_GYM}
        />
        <BooleanField
          source="usesEligibilityServiceType"
          data-testid="usesEligibilityServiceType"
          label={Labels.USES_ELIGIBILITY_SERVICE_TYPE}
        />

        <Typography variant="subtitle2">
          {Labels.SUBTITLE_OPT_IN_FEATURES}
        </Typography>
        <BooleanField
          source="usesPersonalTraining"
          data-testid="usesPersonalTraining"
          label={Labels.USES_PERSONAL_TRAINING}
          valueLabelTrue="Supports personal training sessions"
          valueLabelFalse="Does not support personal training sessions"
        />
        <BooleanField
          source="usesHighPerformanceCoaching"
          data-testid="usesHighPerformanceCoaching"
          label={Labels.USES_HIGH_PERFORMANCE_COACHING}
          valueLabelTrue="Supports high-performance coaching"
          valueLabelFalse="Does not support high-performance coaching"
        />
        <BooleanField
          source="usesMboRegistrationFlow"
          data-testid="usesMboRegistrationFlow"
          label={Labels.USES_MBO_REGISTRATION_FLOW}
        />
        <BooleanField
          source="usesMboSso"
          data-testid="usesMboSso"
          label={Labels.USES_MBO_SSO}
        />
        <BooleanField
          source="usesCustomChallenges"
          data-testid="usesCustomChallenges"
          label={Labels.USES_CUSTOM_CHALLENGES}
        />

        <Typography variant="subtitle2">
          {Labels.SUBTITLE_CLIENT_OFFERINGS}
        </Typography>
        <NumberField
          source="maxFamilyMembers"
          label={Labels.MAX_FAMILY_MEMBERS}
          data-testid="maxFamilyMembers"
          emptyText="0"
        />
        <BooleanField
          source="collectsMemberData"
          data-testid="collectsMemberData"
          label={Labels.COLLECTS_MEMBER_DATA}
        />
        <BooleanField
          source="collectsMemberDataForEEP"
          data-testid="collectsMemberDataForEEP"
          label={Labels.COLLECTS_MEMBER_DATA_FOR_EEP}
        />

        <Typography variant="subtitle2">
          {Labels.SUBTITLE_OTHER_CONFIGS}
        </Typography>
        <BooleanField
          source="allowsCoachesToCreateMembers"
          label={Labels.ALLOWS_COACHES_TO_CREATE_MEMBERS}
          data-testid="allowsCoachesToCreateMembers"
        />
        <BooleanField
          source="allowsInAppTransactions"
          label={Labels.ALLOWS_IN_APP_TRANSACTIONS}
          data-testid="allowsInAppTransactions"
        />
        <NumberField
          source="inactivityDuration"
          data-testid="inactivityDuration"
          label={Labels.INACTIVITY_DURATION}
          emptyText="N/A"
        />
        <BooleanField
          source="strictAppCheck"
          label={Labels.STRICT_APP_CHECK}
          data-testid="strictAppCheck"
        />
        <BooleanField
          source="needsUsToFixTheirUserNames"
          data-testid="needsUsToFixTheirUserNames"
          label={Labels.NEEDS_US_TO_FIX_THEIR_USERNAMES}
          valueLabelTrue="Names need to be reformatted (ex. BRAD -> Brad)"
          valueLabelFalse="Names are already correctly formatted (ex. Brad not BRAD)"
        />
      </Tab>
      <Tab label="Coaches" data-testid="tab-Coaches">
        <ClientCoachTab />
      </Tab>
      <Tab label="Diagnostics" data-testid="tab-Diagnostics">
        <DiagnosticsTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
